import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BackButton from "../components/BackButton";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  fetchActowizFeatures,
  fetchActowizProducts,
  fetchActowizTopDeals,
} from "../store/actowiz/actowizSlice";
import Rectangle1 from "../assets/sainsburys.svg";
import Rectangle2 from "../assets/tesco.svg";
import Rectangle3 from "../assets/asda.svg";
import Rectangle4 from "../assets/morrisons.svg";
import { loadPasteListFlow } from "../utils/pasteListFlow";

const parseMoney = (value) => {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const cleaned = String(value).trim().replaceAll(",", ".").replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Treat 0 / negative as missing so we can fall back to offer_price. */
const positiveMoney = (value) => {
  const parsed = parseMoney(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const parseRatingValue = (value) => {
  if (value == null) return null;
  const parsed = Number.parseFloat(String(value).trim().replace(",", ".").replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const parseReviewCountValue = (value) => {
  if (value == null) return null;
  const parsed = Number.parseInt(String(value).replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const matchesId = (p, matchId) => {
  if (!p || matchId == null) return false;
  const candidateIds = [
    p?.pid,
    p?.product_id,
    p?.sku,
    p?.id,
    p?.productId,
    p?.productID,
  ]
    .map((v) => (v == null ? "" : String(v)))
    .filter(Boolean);
  return candidateIds.includes(String(matchId));
};

const normalizePlatformKey = (platform) => {
  const p = String(platform || "").trim().toLowerCase();
  // Actowiz / providers may return variants like "ASDA Groceries", "Sainsbury’s", etc.
  if (!p) return "";
  if (p.includes("tesco")) return "tesco";
  if (p.includes("morris")) return "morrison";
  if (p.includes("asda")) return "asda";
  if (p.includes("sains")) return "sainsbury";
  if (p === "sainsburry") return "sainsbury";
  return "";
};

const getProductImages = (prod) => {
  if (!prod) return [];
  if (Array.isArray(prod.images) && prod.images.length) {
    return prod.images.filter(Boolean);
  }
  return [prod.image, prod.image_url, prod.imageUrl, prod.thumbnail, prod.thumb].filter(Boolean);
};

const pickVisitUrl = (obj) =>
  obj?.url ||
  obj?.product_url ||
  obj?.productUrl ||
  obj?.store_url ||
  obj?.storeUrl ||
  obj?.visit_url ||
  obj?.visitUrl ||
  obj?.deep_link ||
  obj?.deepLink ||
  obj?.link ||
  null;

// Same retailer banners as home page `SupermarketTabs` (Rectangle1–4).
const retailerBannerByPlatform = {
  sainsbury: Rectangle1,
  tesco: Rectangle2,
  asda: Rectangle3,
  morrison: Rectangle4,
};

// Note: `actowiz/search` helpers were removed because this page now reuses
// `product.comparison` from already-fetched product list responses.

function ProductDetail({ onOpenAuth, onOpenMenu }) {
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const hydrateAttemptedForIdRef = useRef(null);
  const byPlatform = useAppSelector((s) => s.actowiz.byPlatform);
  const [selectedSize, setSelectedSize] = useState("");
  const topDeals = useAppSelector((s) => s.actowiz.topDeals);
  const features = useAppSelector((s) => s.actowiz.features);
  const [isHydrating, setIsHydrating] = useState(false);
  const readCachedProduct = useCallback((productId) => {
    if (!productId) return null;
    try {
      const raw = window.sessionStorage.getItem(`product-detail-cache:${productId}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }, []);

  const readCachedPricingMode = useCallback((productId) => {
    if (!productId) return "";
    try {
      return String(window.sessionStorage.getItem(`product-detail-pricing-mode:${productId}`) || "");
    } catch {
      return "";
    }
  }, []);

  const [cachedProduct, setCachedProduct] = useState(() => readCachedProduct(id));
  const [cachedPricingMode, setCachedPricingMode] = useState(() => readCachedPricingMode(id));

  useEffect(() => {
    setCachedProduct(readCachedProduct(id));
    setCachedPricingMode(readCachedPricingMode(id));
  }, [id, readCachedProduct, readCachedPricingMode]);

  const product = useMemo(() => {
    if (!id) return null;

    const routeProduct = location.state?.product;
    if (routeProduct && matchesId(routeProduct, id)) {
      // Prefer grocery DB snapshot (platform_data → comparison) over live catalog.
      if (routeProduct.fromGrocery || location.state?.fromGrocery) {
        const comparison =
          Array.isArray(routeProduct.comparison) && routeProduct.comparison.length
            ? routeProduct.comparison
            : Array.isArray(routeProduct.platform_data)
              ? routeProduct.platform_data
              : [];
        return {
          ...routeProduct,
          comparison,
          fromGrocery: true,
        };
      }
      return routeProduct;
    }

    const actowizAll = [
      ...(Array.isArray(topDeals) ? topDeals : []),
      ...(Array.isArray(features) ? features : []),
      ...(byPlatform?.tesco || []),
      ...(byPlatform?.morrison || []),
      ...(byPlatform?.asda || []),
      ...(byPlatform?.sainsbury || []),
    ];
    const fromActowiz =
      actowizAll.find((p) => matchesId(p, id)) ||
      actowizAll.find((p) => p?.sku != null && String(p.sku) === String(id));
    if (fromActowiz) return fromActowiz;

    if (cachedProduct && matchesId(cachedProduct, id)) return cachedProduct;

    const flow = loadPasteListFlow();
    const pasteProducts = Array.isArray(flow?.products) ? flow.products : [];
    const fromPasteFlow =
      pasteProducts.find((p) => matchesId(p, id)) ||
      pasteProducts.find((p) => p?.sku != null && String(p.sku) === String(id));
    if (fromPasteFlow) return fromPasteFlow;

    return null;
  }, [id, byPlatform, topDeals, features, cachedProduct, location.state]);

  const isGrocerySnapshot = Boolean(
    product?.fromGrocery || location.state?.fromGrocery
  );

  const isOfferMode = useMemo(() => {
    const fromState =
      location.state?.pricingMode ||
      location.state?.priceMode ||
      (location.state?.offer === true ? "Loyalty+Offer" : "");
    if (/offer|loyalty/i.test(String(fromState || ""))) return true;
    if (location.state?.pricingMode === "Base" || location.state?.offer === false) return false;
    if (/offer|loyalty/i.test(String(cachedPricingMode || ""))) return true;
    return false;
  }, [location.state, cachedPricingMode]);

  useEffect(() => {
    if (!id || !product) return;
    try {
      window.sessionStorage.setItem(`product-detail-cache:${id}`, JSON.stringify(product));
      if (isOfferMode) {
        window.sessionStorage.setItem(`product-detail-pricing-mode:${id}`, "Loyalty+Offer");
        setCachedPricingMode("Loyalty+Offer");
      } else if (location.state?.pricingMode === "Base" || location.state?.offer === false) {
        window.sessionStorage.removeItem(`product-detail-pricing-mode:${id}`);
        setCachedPricingMode("");
      }
    } catch {
      // Ignore cache write failures.
    }
  }, [id, product, isOfferMode, location.state?.pricingMode, location.state?.offer]);

  useEffect(() => {
    if (!id || product) return;
    if (isGrocerySnapshot) return;
    if (hydrateAttemptedForIdRef.current === id) return;

    hydrateAttemptedForIdRef.current = id;
    let cancelled = false;

    setIsHydrating(true);
    (async () => {
      try {
        await Promise.all([
          dispatch(fetchActowizTopDeals({ start: 0, offset: 60, mode: "replace", force: true })),
          dispatch(
            fetchActowizFeatures({
              start: 0,
              offset: 60,
              mode: "replace",
              force: true,
              offer: isOfferMode || undefined,
            })
          ),
          dispatch(
            fetchActowizProducts({
              platform: "tesco",
              start: 0,
              offset: 60,
              mode: "replace",
              force: true,
            })
          ),
          dispatch(
            fetchActowizProducts({
              platform: "morrison",
              start: 0,
              offset: 60,
              mode: "replace",
              force: true,
            })
          ),
          dispatch(
            fetchActowizProducts({
              platform: "asda",
              start: 0,
              offset: 60,
              mode: "replace",
              force: true,
            })
          ),
          dispatch(
            fetchActowizProducts({
              platform: "sainsbury",
              start: 0,
              offset: 60,
              mode: "replace",
              force: true,
            })
          ),
        ]);
      } finally {
        if (!cancelled) setIsHydrating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, product, dispatch, isOfferMode, isGrocerySnapshot]);

  useEffect(() => {
    hydrateAttemptedForIdRef.current = null;
  }, [id]);

  const comparisonByPlatform = useMemo(() => {
    if (!product?.comparison) return null;

    const base = { tesco: null, morrison: null, asda: null, sainsbury: null };
    const comp = product.comparison;

    const pickOfferPrice = (x) => {
      const fromOffers = Array.isArray(x?.offers) ? x.offers[0] : null;
      return positiveMoney(
        x?.offer_price ??
          x?.offerPrice ??
          x?.loyalty_pricing ??
          fromOffers?.offer_price ??
          fromOffers?.price
      );
    };
    const pickBasePrice = (x) =>
      positiveMoney(x?.price ?? x?.unit_price ?? x?.price_per_unit ?? x?.pricePerUnit);
    const pickPromotion = (x) => {
      const fromOffers = Array.isArray(x?.offers) ? x.offers[0] : null;
      return String(
        x?.promotion ||
          fromOffers?.desc ||
          fromOffers?.description ||
          fromOffers?.longDescription ||
          fromOffers?.title ||
          fromOffers?.text ||
          ""
      ).trim();
    };

    // Expected shape from this codebase: [{ platform, price }, ...]
    if (Array.isArray(comp)) {
      comp.forEach((x) => {
        const key = normalizePlatformKey(
          x?.platform || x?.retailer || x?.provider || x?.store || x?.site
        );
        if (!key) return;
        const url = pickVisitUrl(x);
        const basePrice = pickBasePrice(x);
        const offerPrice = pickOfferPrice(x);
        const promotion = pickPromotion(x);
        // If base price is 0/missing, fall back to offer_price for display amount.
        const price = isOfferMode
          ? offerPrice ?? (promotion ? basePrice : null) ?? basePrice
          : basePrice ?? offerPrice;
        const match_type = String(x?.match_type ?? x?.matchType ?? "").trim() || "";
        const rating = parseRatingValue(
          x?.average_rating ?? x?.avg_rating ?? x?.rating ?? x?.review_rating
        );
        const reviewCount = parseReviewCountValue(
          x?.review_count ?? x?.reviews ?? x?.rating_count ?? x?.reviewCount
        );
        const product_name = String(x?.product_name || "").trim();
        const prev = base[key];
        if (Number.isFinite(price) || Number.isFinite(basePrice) || Number.isFinite(offerPrice)) {
          if (prev == null || !Number.isFinite(prev?.price) || (Number.isFinite(price) && price < prev.price)) {
            base[key] = {
              price: Number.isFinite(price) ? price : null,
              basePrice: Number.isFinite(basePrice) ? basePrice : null,
              offerPrice: Number.isFinite(offerPrice) ? offerPrice : null,
              promotion,
              url,
              rating,
              reviewCount,
              match_type,
              product_name,
            };
          } else if (!prev?.url && url) {
            base[key] = { ...prev, url };
          } else if (prev?.rating == null && rating != null) {
            base[key] = { ...prev, rating };
          } else if (prev?.reviewCount == null && reviewCount != null) {
            base[key] = { ...prev, reviewCount };
          } else if (!prev?.match_type && match_type) {
            base[key] = { ...prev, match_type };
          } else if (!prev?.promotion && promotion) {
            base[key] = { ...prev, promotion };
          } else if (prev?.offerPrice == null && Number.isFinite(offerPrice)) {
            base[key] = { ...prev, offerPrice };
          }
        } else if (url && prev == null) {
          base[key] = {
            price: null,
            basePrice: null,
            offerPrice: null,
            promotion,
            url,
            rating,
            reviewCount,
            match_type,
            product_name,
          };
        }
      });
      return base;
    }

    // If API ever returns an object map already.
    if (typeof comp === "object" && comp) {
      (Object.keys(base) || []).forEach((k) => {
        if (Object.prototype.hasOwnProperty.call(comp, k)) {
          const value = comp[k];
          const basePrice =
            value && typeof value === "object"
              ? pickBasePrice(value)
              : positiveMoney(value);
          const offerPrice = value && typeof value === "object" ? pickOfferPrice(value) : null;
          const promotion = value && typeof value === "object" ? pickPromotion(value) : "";
          // If base price is 0/missing, fall back to offer_price for display amount.
          const price = isOfferMode
            ? offerPrice ?? (promotion ? basePrice : null) ?? basePrice
            : basePrice ?? offerPrice;
          const url = value && typeof value === "object" ? pickVisitUrl(value) : null;
          const rating =
            value && typeof value === "object"
              ? parseRatingValue(
                  value?.average_rating ?? value?.avg_rating ?? value?.rating ?? value?.review_rating
                )
              : null;
          const reviewCount =
            value && typeof value === "object"
              ? parseReviewCountValue(
                  value?.review_count ?? value?.reviews ?? value?.rating_count ?? value?.reviewCount
                )
              : null;
          const match_type =
            value && typeof value === "object"
              ? String(value?.match_type ?? value?.matchType ?? "").trim()
              : "";
          const product_name =
            value && typeof value === "object" ? String(value?.product_name || "").trim() : "";
          if (Number.isFinite(price) || Number.isFinite(basePrice) || Number.isFinite(offerPrice) || url) {
            base[k] = {
              price: Number.isFinite(price) ? price : null,
              basePrice: Number.isFinite(basePrice) ? basePrice : null,
              offerPrice: Number.isFinite(offerPrice) ? offerPrice : null,
              promotion,
              url,
              rating,
              reviewCount,
              match_type: match_type || "",
              product_name,
            };
          }
        }
      });
      return base;
    }

    return null;
  }, [product, isOfferMode]);

  const retailerStack = useMemo(() => {
    const platforms = ["tesco", "morrison", "asda", "sainsbury"];
    const labelByPlatform = {
      tesco: { label: "Tesco", color: "#1359EF" },
      morrison: { label: "Morrisons", color: "#A20886" },
      asda: { label: "Asda", color: "#08A237" },
      sainsbury: { label: "Sainsbury's", color: "#EC570D" },
    };

    // Grocery list detail: render exactly what was saved in platform_data.
    if (isGrocerySnapshot && comparisonByPlatform) {
      const rows = platforms.map((platform) => {
        const entry = comparisonByPlatform[platform];
        const rawPrice = isOfferMode
          ? positiveMoney(entry?.offerPrice) ?? positiveMoney(entry?.price)
          : positiveMoney(entry?.price) ?? positiveMoney(entry?.offerPrice);
        const price = Number.isFinite(rawPrice) ? rawPrice : null;
        return {
          platform,
          ...labelByPlatform[platform],
          retailerBannerSrc: retailerBannerByPlatform[platform] || null,
          price,
          basePrice: positiveMoney(entry?.basePrice) ?? positiveMoney(entry?.price),
          offerPrice: positiveMoney(entry?.offerPrice),
          promotion: entry?.promotion || "",
          product_name: entry?.product_name || "",
          formatted: price != null ? `£${price.toFixed(2)}` : "—",
          visitUrl: entry?.url || null,
          rating: Number.isFinite(entry?.rating) ? entry.rating : null,
          reviewCount: Number.isFinite(entry?.reviewCount) ? entry.reviewCount : null,
          match_type: entry?.match_type || "",
        };
      });
      const prices = rows.map((r) => r.price).filter((p) => typeof p === "number" && Number.isFinite(p) && p > 0);
      const cheapest = prices.length ? Math.min(...prices) : null;
      const highest = prices.length ? Math.max(...prices) : null;
      const savings = cheapest != null && highest != null ? highest - cheapest : null;
      const hasPriceSpread = savings != null && savings > 0;
      const cheapestRow =
        hasPriceSpread ? rows.find((r) => r.price === cheapest) || null : null;
      const lowestPlatforms = hasPriceSpread
        ? new Set(rows.filter((r) => r.price === cheapest).map((r) => r.platform))
        : new Set();
      return {
        rows,
        cheapestPrice: cheapest,
        cheapestRow,
        lowestPlatforms,
        savings: hasPriceSpread ? savings : null,
      };
    }

    const normalizeIdentityText = (value) => String(value || "").trim().toLowerCase();
    const currentSku = normalizeIdentityText(product?.sku);
    const currentTitle = normalizeIdentityText(product?.title);
    const allKnownProducts = [
      ...(Array.isArray(topDeals) ? topDeals : []),
      ...(Array.isArray(features) ? features : []),
      ...(byPlatform?.tesco || []),
      ...(byPlatform?.morrison || []),
      ...(byPlatform?.asda || []),
      ...(byPlatform?.sainsbury || []),
    ];
    const match = (p) => matchesId(p, id);
    const sameProductFallback = (candidate) => {
      if (!candidate) return false;
      if (match(candidate)) return true;
      const candidateSku = normalizeIdentityText(candidate?.sku);
      const candidateTitle = normalizeIdentityText(candidate?.title || candidate?.product_name);
      if (currentSku && candidateSku && candidateSku === currentSku) return true;
      if (currentTitle && candidateTitle && candidateTitle === currentTitle) return true;
      return false;
    };
    const sameProductLoose = (candidate) => {
      if (!candidate) return false;
      if (sameProductFallback(candidate)) return true;
      const candidateSku = normalizeIdentityText(candidate?.sku);
      const candidateTitle = normalizeIdentityText(candidate?.title || candidate?.product_name);
      if (currentSku && candidateSku && (candidateSku.includes(currentSku) || currentSku.includes(candidateSku))) {
        return true;
      }
      if (
        currentTitle &&
        candidateTitle &&
        (candidateTitle.includes(currentTitle) || currentTitle.includes(candidateTitle))
      ) {
        return true;
      }
      return false;
    };
    // Derive current retailer key as robustly as possible so "current price" can be shown even
    // when cross-store match isn't available.
    const rawPlatform = product?.platform || product?.retailer || product?.provider || "";
    const currentPlatformKey = (() => {
      const direct =
        normalizePlatformKey(rawPlatform) ||
        (platforms.includes(String(rawPlatform || "").trim().toLowerCase())
          ? String(rawPlatform || "").trim().toLowerCase()
          : "");
      if (direct) return direct;

      // If API didn't provide a platform but we came from a specific retailer list, infer it.
      for (const k of platforms) {
        const list = byPlatform?.[k] || [];
        if (Array.isArray(list) && list.some(match)) return k;
      }
      return "";
    })();

    const rows = platforms.map((platform) => {
      const list = byPlatform?.[platform] || [];
      const found = Array.isArray(list) ? list.find(sameProductFallback) : null;
      const peer = allKnownProducts.find(
        (candidate) => normalizePlatformKey(candidate?.platform) === platform && sameProductLoose(candidate)
      );
      const raw =
        found?.price ??
        found?.unit_price ??
        found?.price_per_unit ??
        found?.pricePerUnit ??
        found?.offer_price ??
        found?.offerPrice ??
        null;
      const price = parseMoney(raw);
      const comparisonEntry =
        comparisonByPlatform && Object.prototype.hasOwnProperty.call(comparisonByPlatform, platform)
          ? comparisonByPlatform[platform]
          : null;
      const comparisonPrice =
        comparisonEntry && typeof comparisonEntry === "object"
          ? comparisonEntry?.price
          : comparisonEntry;
      const comparisonUrl =
        comparisonEntry && typeof comparisonEntry === "object" ? comparisonEntry?.url : null;
      const comparisonRating =
        comparisonEntry && typeof comparisonEntry === "object"
          ? comparisonEntry?.rating ?? comparisonEntry?.average_rating
          : null;
      const comparisonReviewCount =
        comparisonEntry && typeof comparisonEntry === "object"
          ? comparisonEntry?.reviewCount ?? comparisonEntry?.review_count ?? comparisonEntry?.reviews
          : null;
      const comparisonMatchType =
        comparisonEntry && typeof comparisonEntry === "object"
          ? String(comparisonEntry?.match_type ?? comparisonEntry?.matchType ?? "").trim()
          : "";
      const comparisonOfferPrice =
        comparisonEntry && typeof comparisonEntry === "object"
          ? positiveMoney(comparisonEntry?.offerPrice ?? comparisonEntry?.offer_price)
          : null;
      const comparisonBasePrice =
        comparisonEntry && typeof comparisonEntry === "object"
          ? positiveMoney(comparisonEntry?.basePrice ?? comparisonEntry?.price)
          : null;
      const comparisonPromotion =
        comparisonEntry && typeof comparisonEntry === "object"
          ? String(comparisonEntry?.promotion || "").trim()
          : "";
      const comparisonProductName =
        comparisonEntry && typeof comparisonEntry === "object"
          ? String(comparisonEntry?.product_name || "").trim()
          : "";
      const finalMatchType =
        comparisonMatchType ||
        String(found?.match_type ?? found?.matchType ?? found?.response?.match_type ?? "").trim() ||
        String(peer?.match_type ?? peer?.matchType ?? peer?.response?.match_type ?? "").trim() ||
        "";
      let finalPrice = null;
      if (isOfferMode) {
        finalPrice =
          comparisonOfferPrice ??
          (comparisonPromotion ? positiveMoney(comparisonPrice) : null) ??
          positiveMoney(comparisonPrice) ??
          positiveMoney(found?.offer_price ?? found?.offerPrice) ??
          positiveMoney(peer?.offer_price ?? peer?.offerPrice) ??
          null;
      } else {
        // If base price is 0/missing, fall back to offer_price.
        finalPrice =
          positiveMoney(comparisonPrice) ??
          comparisonOfferPrice ??
          positiveMoney(price) ??
          positiveMoney(found?.offer_price ?? found?.offerPrice) ??
          positiveMoney(peer?.offer_price ?? peer?.offerPrice);
      }
      let finalUrl = pickVisitUrl(found);
      if (!finalUrl && comparisonUrl) finalUrl = comparisonUrl;
      if (!finalUrl && currentPlatformKey && platform === currentPlatformKey) {
        finalUrl = pickVisitUrl(product);
      }
      // If the store lists aren't loaded (or don't contain this pid), at least show the current product's retailer price.
      if (finalPrice == null && currentPlatformKey && platform === currentPlatformKey) {
        finalPrice = isOfferMode
          ? positiveMoney(product?.offer_price) ??
            positiveMoney(
              Array.isArray(product?.offers)
                ? product.offers[0]?.offer_price ?? product.offers[0]?.price
                : null
            ) ??
            positiveMoney(product?.price)
          : positiveMoney(
              product?.price ?? product?.unit_price ?? product?.price_per_unit ?? product?.pricePerUnit
            ) ??
            positiveMoney(product?.offer_price) ??
            positiveMoney(
              Array.isArray(product?.offers)
                ? product.offers[0]?.offer_price ?? product.offers[0]?.price
                : null
            );
      }
      const basePrice =
        comparisonBasePrice ??
        parseMoney(found?.price ?? peer?.price) ??
        (platform === currentPlatformKey ? parseMoney(product?.price) : null);
      const offerPrice =
        comparisonOfferPrice ??
        parseMoney(found?.offer_price ?? found?.offerPrice ?? peer?.offer_price ?? peer?.offerPrice) ??
        (platform === currentPlatformKey
          ? parseMoney(
              product?.offer_price ??
                (Array.isArray(product?.offers)
                  ? product.offers[0]?.offer_price ?? product.offers[0]?.price
                  : null)
            )
          : null);
      const promotion =
        comparisonPromotion ||
        String(found?.promotion || peer?.promotion || "").trim() ||
        (platform === currentPlatformKey && Array.isArray(product?.offers)
          ? String(product.offers[0]?.desc || product.offers[0]?.description || "").trim()
          : "");
      const productLevelRating = parseRatingValue(
        product?.average_rating ?? product?.avg_rating ?? product?.rating
      );
      const productLevelReviewCount = parseReviewCountValue(
        product?.review_count ?? product?.reviews ?? product?.reviewCount ?? product?.rating_count
      );
      const ratingSource =
        found?.average_rating ??
        found?.rating ??
        peer?.average_rating ??
        peer?.rating ??
        comparisonRating ??
        (platform === currentPlatformKey ? productLevelRating : null);
      const parsedRating = parseRatingValue(ratingSource);
      const reviewCountSource =
        found?.review_count ??
        found?.reviews ??
        found?.reviewCount ??
        peer?.review_count ??
        peer?.reviews ??
        peer?.reviewCount ??
        comparisonReviewCount ??
        (platform === currentPlatformKey ? productLevelReviewCount : null);
      const parsedReviewCount = parseReviewCountValue(reviewCountSource);
      return {
        platform,
        ...labelByPlatform[platform],
        retailerBannerSrc: retailerBannerByPlatform[platform] || null,
        price: finalPrice,
        basePrice: Number.isFinite(basePrice) ? basePrice : null,
        offerPrice: Number.isFinite(offerPrice) ? offerPrice : null,
        promotion,
        product_name: comparisonProductName,
        formatted: finalPrice != null ? `£${finalPrice.toFixed(2)}` : "—",
        visitUrl: finalUrl,
        rating: Number.isFinite(parsedRating) ? parsedRating : null,
        reviewCount: Number.isFinite(parsedReviewCount) ? parsedReviewCount : null,
        match_type: finalMatchType,
      };
    });

    const prices = rows.map((r) => r.price).filter((p) => typeof p === "number" && Number.isFinite(p));
    const cheapest = prices.length ? Math.min(...prices) : null;
    const highest = prices.length ? Math.max(...prices) : null;
    const savings = cheapest != null && highest != null ? highest - cheapest : null;
    // Badge every store at the min price when at least one store is higher.
    // Hide badges entirely when all prices are equal.
    const hasPriceSpread = savings != null && savings > 0;
    const cheapestRow =
      hasPriceSpread ? rows.find((r) => r.price === cheapest) || null : null;
    const lowestPlatforms = hasPriceSpread
      ? new Set(rows.filter((r) => r.price === cheapest).map((r) => r.platform))
      : new Set();

    return {
      rows,
      cheapestPrice: cheapest,
      cheapestRow,
      lowestPlatforms,
      savings: hasPriceSpread ? savings : null,
    };
  }, [byPlatform, topDeals, features, id, comparisonByPlatform, product, isOfferMode, isGrocerySnapshot]);

  const heroPrice = useMemo(() => {
    if (retailerStack.cheapestRow?.price != null) return retailerStack.cheapestRow.price;
    if (retailerStack.cheapestPrice != null) return retailerStack.cheapestPrice;
    return parseMoney(product?.price);
  }, [retailerStack.cheapestRow?.price, retailerStack.cheapestPrice, product?.price]);

  const heroPriceLabel = retailerStack.cheapestRow?.label || null;
  const heroRetailerBanner = retailerStack.cheapestRow?.retailerBannerSrc || null;

  const images = useMemo(() => getProductImages(product), [product]);
  const [selectedImage, setSelectedImage] = useState("");
  useEffect(() => {
    setSelectedImage((prev) => {
      if (prev && images.includes(prev)) return prev;
      return images[0] || "";
    });
  }, [id, images]);

  // Prefer rating from the lowest-price store when available.
  const ratingAvg =
    retailerStack.cheapestRow?.rating ??
    product?.average_rating ??
    product?.rating;
  const ratingCount =
    retailerStack.cheapestRow?.reviewCount ??
    product?.review_count;
  const ratingParsed =
    ratingAvg == null
      ? null
      : Number.parseFloat(String(ratingAvg).trim().replace(",", "."));
  const reviewCountParsed = Number.parseInt(String(ratingCount), 10);
  const ratingStars =
    typeof ratingParsed === "number" && Number.isFinite(ratingParsed) ? ratingParsed.toFixed(1) : null;

  const sizeOptions = useMemo(() => {
    const raw = String(product?.packsize || "").trim();
    const list = raw
      ? raw
          .split(/[,/|]/g)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    return list.length ? list.slice(0, 6) : ["—"];
  }, [product?.packsize]);

  useEffect(() => {
    if (!sizeOptions.length) return;
    setSelectedSize((prev) => (prev && sizeOptions.includes(prev) ? prev : sizeOptions[0] || ""));
  }, [id, sizeOptions]);

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A]">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="w-full min-w-0 overflow-x-hidden px-3 py-4 sm:px-4 sm:py-6 md:px-6 md:py-8">
        <div className="mx-auto w-full max-w-8xl">
          <div className="mb-4 rounded-2xl border border-[#E6EAF0] bg-white p-3 shadow-sm sm:mb-6 sm:p-5">
            <BackButton />
          </div>
          {!product && (
            <div className="mt-12 rounded-2xl border border-[#E6EAF0] bg-white p-5 text-center text-[16px] text-[#6E7B86] shadow-sm sm:mt-20">
              {isHydrating ? "Loading product details..." : "Product not found."}
            </div>
          )}

          <div className="grid min-w-0 gap-4 sm:gap-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] lg:gap-8 xl:grid-cols-[minmax(0,500px)_minmax(0,1fr)]">
            <div className="min-w-0 space-y-3 sm:space-y-4">
              <div className="flex items-center justify-center overflow-hidden rounded-2xl border border-[#E6EAF0] bg-white p-3 shadow-sm sm:p-4">
                {selectedImage ? (
                  <img
                    src={selectedImage}
                    alt={product?.title || product?.sku}
                    className="h-auto max-h-56 w-full max-w-full object-contain sm:max-h-72"
                  />
                ) : (
                  <div className="flex h-48 w-full items-center justify-center rounded-xl bg-[#F5F7FB] text-[14px] text-[#6E7B86] sm:h-72 sm:text-[16px]">
                    No Image
                  </div>
                )}
              </div>

              {images.length > 1 && (
                <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 sm:gap-4 sm:p-3">
                  {images.map((src, idx) => {
                    const isActive = src === selectedImage;
                    return (
                      <button
                        key={`${src}-${idx}`}
                        type="button"
                        onClick={() => setSelectedImage(src)}
                        className={[
                          "flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-white sm:h-24 sm:w-24",
                          isActive ? "border-[#7352C7] ring-2 ring-[#7352C7]/20" : "border-[#E6EAF0]",
                        ].join(" ")}
                        aria-label={`View image ${idx + 1}`}
                        aria-pressed={isActive}
                      >
                        <img
                          src={src}
                          alt={`${product?.title || product?.sku || "Product"} thumbnail ${idx + 1}`}
                          className="h-full w-full object-contain bg-white p-1.5 sm:p-4"
                          loading="lazy"
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="min-w-0 space-y-4 rounded-2xl border border-[#E6EAF0] bg-white p-3 shadow-sm sm:space-y-6 sm:p-6 sm:rounded-3xl">
              <div className="flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-start sm:gap-4">
                <div className="min-w-0 flex-1">
                  <h1 className="break-words text-[20px] font-bold leading-tight text-[#24303A] sm:text-[28px]">
                    {product?.title || product?.sku || "N/A"}
                  </h1>
                  <div className="mt-2 flex flex-wrap items-center gap-2.5 text-[16px] font-bold sm:gap-3 sm:text-[18px]">
                    {(ratingStars != null || Number.isFinite(reviewCountParsed)) && (
                      <span className="rounded-full bg-[#7352C7]/10 px-4 py-2 font-semibold text-[#7352C7] sm:px-5 sm:py-2.5 sm:text-[18px]">
                        {ratingStars != null ? `★ ${ratingStars}` : "★"}
                        {Number.isFinite(reviewCountParsed) ? ` | ${reviewCountParsed} reviews` : ""}
                      </span>
                    )}
                    {!isOfferMode &&
                      retailerStack.cheapestRow?.visitUrl &&
                      Number.isFinite(retailerStack.cheapestRow?.price) &&
                      retailerStack.cheapestRow.price > 0 && (
                        <button
                          type="button"
                          className="rounded-full bg-[#7352C71A] px-4 py-2 text-[16px] font-semibold text-[#7352C7] sm:px-5 sm:py-2.5 sm:text-[18px]"
                          onClick={() => {
                            window.open(
                              retailerStack.cheapestRow.visitUrl,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }}
                        >
                          Visit Store
                        </button>
                      )}
                  </div>
                </div>

                {!isOfferMode && retailerStack.cheapestRow && (
                  <div className="w-full shrink-0 rounded-2xl border border-emerald-100 bg-emerald-50 px-3 py-2.5 text-left text-[16px] sm:w-auto sm:min-w-[180px] sm:px-4 sm:py-3 sm:text-right">
                    <p className="font-normal text-[#2FC98D] text-[16px]">
                      Lowest price at
                    </p>
                    {heroRetailerBanner ? (
                      <div className="mt-1 flex sm:justify-end">
                        <div className="inline-flex h-10 w-24 items-center justify-center overflow-hidden rounded-md">
                          <img
                            src={heroRetailerBanner}
                            alt={heroPriceLabel || "Store"}
                            className="h-full w-full object-cover object-center"
                            loading="lazy"
                          />
                        </div>
                      </div>
                    ) : heroPriceLabel ? (
                      <span className="break-words font-normal text-[#24303A] text-[16px]">
                        {heroPriceLabel}
                      </span>
                    ) : null}
                    <p className="mt-1 text-[22px] font-bold text-[#2FC98D] sm:text-[28px]">
                      {heroPrice != null ? `£${heroPrice.toFixed(2)}` : "£—"}
                    </p>
                    {retailerStack.savings != null && (
                      <p className="mt-1 text-[15px] font-semibold text-[#2FC98D]">
                        Save £{retailerStack.savings.toFixed(2)} vs highest
                      </p>
                    )}
                  </div>
                )}
              </div>

              <section className="min-w-0 space-y-3">
                <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-end sm:gap-3">
                  <h2 className="text-[18px] font-semibold text-[#24303A] sm:text-[20px]">
                    Other Retailers
                  </h2>
                </div>
                <div className="min-w-0 space-y-2">
                  {retailerStack.rows
                    .filter((row) => {
                      if (isOfferMode) return true;
                      const topLowestPlatform = retailerStack.cheapestRow?.platform;
                      if (!topLowestPlatform) return true;
                      return row.platform !== topLowestPlatform;
                    })
                    .map((row) => (
                      <RetailRow
                        key={row.platform}
                        row={row}
                        isBest={
                          !isOfferMode &&
                          retailerStack.lowestPlatforms?.has(row.platform) &&
                          row.platform !== retailerStack.cheapestRow?.platform
                        }
                        offerMode={isOfferMode}
                      />
                    ))}
                </div>
              </section>

              <section className="space-y-2 text-[16px]">
                <h2 className="text-[18px] font-semibold text-[#24303A] sm:text-[20px]">
                  Good to know
                </h2>
                <p className="break-words font-normal text-[#6E7B86] text-[16px]">
                  {product?.description
                    ? isGrocerySnapshot
                      ? product.description
                      : product.description.slice(0, 220)
                    : "Product description is not available for this item yet."}
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function RetailRow({ row, isBest, offerMode }) {
  const basePriceNumber = positiveMoney(row?.basePrice ?? row?.price);
  const offerPriceNumber = positiveMoney(row?.offerPrice);
  const priceNumber = offerMode
    ? offerPriceNumber ?? basePriceNumber
    : positiveMoney(row?.price) ?? offerPriceNumber ?? basePriceNumber;
  const hasValidPrice = Number.isFinite(priceNumber) && priceNumber > 0;
  const hasOfferPrice = Number.isFinite(offerPriceNumber);
  const hasRegularPrice = Number.isFinite(basePriceNumber);
  const canVisitStore = Boolean(row?.visitUrl) && (hasOfferPrice || hasRegularPrice || hasValidPrice);
  const isHigherSizeMatch = String(row?.match_type || "").trim() === "Higher size Match";

  if (offerMode) {
    return (
      <div className="relative min-w-0 rounded-xl border border-[#E6EAF0] bg-slate-50 px-3 py-3 sm:px-4">
        <div className="flex flex-col gap-2 min-[340px]:flex-row min-[340px]:items-center min-[340px]:justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-12 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md">
              {row?.retailerBannerSrc ? (
                <img
                  src={row.retailerBannerSrc}
                  alt={row?.label || "Store"}
                  className="h-full w-full object-cover object-center"
                  loading="lazy"
                />
              ) : (
                <span className="truncate px-1 text-[10px] font-semibold text-slate-700">
                  {row?.label || "Store"}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[18px] font-bold text-[#7352C7]">
              {hasOfferPrice ? `£${offerPriceNumber.toFixed(2)}` : "—"}
            </span>
            <button
              type="button"
              className={[
                "rounded-full px-3 py-1.5 text-[14px] font-semibold disabled:cursor-not-allowed sm:text-[16px]",
                canVisitStore
                  ? "text-[#7352C7] bg-[#7352C71A]"
                  : "text-slate-400 bg-[#7352C71A]",
              ].join(" ")}
              onClick={(e) => {
                e.stopPropagation();
                if (!canVisitStore) return;
                window.open(row.visitUrl, "_blank", "noopener,noreferrer");
              }}
              disabled={!canVisitStore}
            >
              Visit Store
            </button>
          </div>
        </div>
        {hasOfferPrice ? (
          <p className="mt-2 font-semibold text-[#284283] text-[16px]">
            {row.promotion || `£${offerPriceNumber.toFixed(2)} offer price`}
          </p>
        ) : (
          <div className="mt-2 space-y-1">
            {hasRegularPrice && (
              <p className="font-semibold text-[#284283] text-[16px]">
                Regular price £{basePriceNumber.toFixed(2)}
              </p>
            )}
            <p className="font-semibold text-[#6E7B86] text-[16px]">No offer price available</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`relative min-w-0 rounded-xl border border-[#E6EAF0] ${
        isBest ? "bg-[#EAFBF3] border-[#2FC98D80]" : "bg-slate-50"
      } px-2.5 py-3 sm:px-4 sm:py-2`}
    >
      {isBest && (
        <span className="absolute left-0 top-0">
          <span className="relative inline-flex h-7 w-7 items-center justify-center">
            <span
              className="absolute inset-0 rounded-br-[10px] rounded-tr-none rounded-l-none bg-[#5B3CC4]"
              aria-hidden
            />
            <svg
              viewBox="0 0 24 24"
              className="relative z-10 h-[24px] w-[24px]"
              fill="none"
              aria-hidden
            >
              <path
                d="M12 3.2l2.55 5.17 5.7.83-4.12 4.02.97 5.68L12 16.48 6.9 18.92l.97-5.68L3.75 9.2l5.7-.83L12 3.2z"
                fill="#FFFFFF"
              />
            </svg>
          </span>
        </span>
      )}

      <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-[minmax(0,140px)_minmax(0,1fr)_auto] md:items-center md:gap-3">
        <div className="flex min-w-0 items-center gap-2 pl-5 sm:pl-6 md:pl-0">
          <div className="flex shrink-0 items-center justify-center overflow-hidden rounded-md h-12 w-24">
            {row?.retailerBannerSrc ? (
              <img
                src={row.retailerBannerSrc}
                alt={row?.label || "Store"}
                className="h-full w-full object-cover object-center"
                loading="lazy"
              />
            ) : (
              <span className="truncate px-1 text-[10px] font-semibold text-slate-700">
                {row?.label || "Store"}
              </span>
            )}
          </div>
          <span
            className={[
              "shrink-0 rounded-full px-2 py-0.5 font-semibold uppercase tracking-wide text-[10px]",
              isBest ? "bg-[#1FA971] text-white" : "hidden md:inline-flex bg-transparent text-transparent",
            ].join(" ")}
          >
            LOWEST PRICE
          </span>
        </div>

        <div className="flex min-w-0 items-center justify-start md:justify-center">
          {(typeof row?.rating === "number" && Number.isFinite(row.rating)) ||
          Number.isFinite(row?.reviewCount) ? (
            <div className="inline-flex max-w-full flex-wrap items-center justify-start gap-1 leading-none md:justify-center">
              {typeof row?.rating === "number" && Number.isFinite(row.rating) && (
                <>
                  <StarRating rating={row.rating} />
                  <span className="font-medium leading-none text-[#7352C7] text-[16px]">
                    {row.rating.toFixed(1)}
                  </span>
                </>
              )}
              {Number.isFinite(row?.reviewCount) && (
                <span className="font-medium leading-none text-[#7352C7] text-[16px]">
                  {row.reviewCount} Reviews
                </span>
              )}
            </div>
          ) : (
            <span className="font-medium leading-none text-[#7352C7] text-[16px]">
              No reviews
            </span>
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between md:justify-end">
          <div className="flex items-center">
            <span
              className={[
                "text-[18px] font-bold",
                isHigherSizeMatch ? "text-[rgb(176,48,28)]" : "text-[#7352C7]",
              ].join(" ")}
            >
              {hasValidPrice ? `£${priceNumber.toFixed(2)}` : "—"}
            </span>
          </div>
          <div className="flex w-full sm:w-auto sm:items-center">
            <button
              type="button"
              className={[
                "rounded-full px-2.5 py-1.5 text-center font-semibold disabled:cursor-not-allowed sm:px-3 sm:py-1 text-[16px]",
                row?.visitUrl && hasValidPrice
                  ? "text-[#7352C7] bg-[#7352C71A]"
                  : "text-slate-400 bg-[#7352C71A]",
              ].join(" ")}
              onClick={(e) => {
                e.stopPropagation();
                if (!row?.visitUrl || !hasValidPrice) return;
                window.open(row.visitUrl, "_blank", "noopener,noreferrer");
              }}
              disabled={!row?.visitUrl || !hasValidPrice}
            >
              Visit Store
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StarRating({ rating }) {
  const clamped = Math.max(0, Math.min(5, rating || 0));
  return (
    <div
      className="inline-flex items-center gap-0.5 leading-none"
      aria-label={`Rating ${clamped.toFixed(1)} out of 5`}
    >
      {[0, 1, 2, 3, 4].map((index) => {
        const fillPercent = Math.max(0, Math.min(1, clamped - index));
        return (
          <span key={index} className="relative inline-flex h-3.5 w-3.5 items-center justify-center leading-none">
            <span className="absolute inset-0 flex items-center justify-center leading-none text-slate-300">★</span>
            <span
              className="absolute inset-0 flex items-center justify-start overflow-hidden leading-none text-amber-400"
              style={{ width: `${fillPercent * 100}%` }}
            >
              ★
            </span>
          </span>
        );
      })}

    </div>
  );
}

export default ProductDetail;

